import {
  LAYOUT_COMPONENT_TYPE,
  PAGE_SLUG,
  PLATFORM_CTA_INTENT,
  buildHomepageAnchorHref,
  buildLocalizedSitePath,
  inferCtaIntentFromHref,
  normalizeHomepageSectionHref,
  normalizePlatformCtaIntent,
  prefixPublicPath,
  publicPathForPageSlug,
  type LayoutBlock,
  type LocalizedString,
  type PageMeta,
  type PlatformCtaIntent,
  type SiteMode,
} from '@web-studio/shared-schemas';
import { pickBlockField } from './localized';

/**
 * Section / CTA intents accepted by the resolver.
 * Platform routing verbs: `section` | `external` | `call`.
 * Legacy demand verbs / aliases still resolve to concrete hashes for SSR fallbacks.
 */
export type SectionHrefIntent =
  | PlatformCtaIntent
  | 'book'
  | 'browse'
  | 'inquire'
  | 'visit'
  | 'order'
  | 'reserve'
  | 'menu'
  | 'contact'
  | 'top'
  | 'route';

const MENU_COMPONENT_TYPES = new Set<string>([
  LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  LAYOUT_COMPONENT_TYPE.dynamicMenuFlow,
  LAYOUT_COMPONENT_TYPE.drinksListStatic,
  LAYOUT_COMPONENT_TYPE.simpleMenuStrip,
]);

const RESERVE_COMPONENT_TYPES = new Set<string>([
  LAYOUT_COMPONENT_TYPE.reservationFormEmbed,
  LAYOUT_COMPONENT_TYPE.reservationThirdPartyBook,
  LAYOUT_COMPONENT_TYPE.reservationMessengerBook,
  LAYOUT_COMPONENT_TYPE.reservationCallBook,
]);

export interface ResolveSectionHrefOptions {
  siteMode?: SiteMode;
  pages?: PageMeta[];
  allBlocks?: LayoutBlock[];
  /** Active visitor locale — prefixes menu page paths when not the site default. */
  locale?: string;
  /** Project site default locale (unprefixed paths). */
  defaultLocale?: string;
  /** Path-based preview prefix (`/api/serve-staging/{id}`). */
  publicPathPrefix?: string | null;
}

function blocksInclude(
  blocks: LayoutBlock[] | undefined,
  types: Set<string>,
): boolean | null {
  if (!blocks || blocks.length === 0) {
    return null;
  }
  return blocks.some((block) => types.has(block.componentType));
}

function sectionAnchorHref(
  anchor: string,
  options: ResolveSectionHrefOptions,
): string {
  const locale = options.locale ?? options.defaultLocale ?? 'de';
  const defaultLocale = options.defaultLocale ?? 'de';
  return buildHomepageAnchorHref(anchor, locale, defaultLocale, options.publicPathPrefix);
}

/**
 * Resolves in-page / route hrefs for CTAs when no content override is stored.
 *
 * Legacy verbs (`book`, `browse`, `reserve`, …) still map to concrete sections.
 * Platform `section` without an override falls back to `#contact` (Zone 4 always
 * writes a live section hash when the owner picks Section).
 * `external` / `call` without a destination also fall back to `#contact`.
 */
export function resolveSectionHref(
  intent: SectionHrefIntent,
  options: ResolveSectionHrefOptions = {},
): string {
  const raw = (intent ?? '').trim();

  if (raw === 'top') {
    return sectionAnchorHref('#top', options);
  }

  // Preserve legacy section-target resolution before collapsing to platform verbs.
  if (raw === 'reserve' || raw === 'book') {
    const hasReserve = blocksInclude(options.allBlocks, RESERVE_COMPONENT_TYPES);
    if (hasReserve === false) {
      return sectionAnchorHref('#contact', options);
    }
    return sectionAnchorHref('#reserve', options);
  }

  if (raw === 'menu' || raw === 'browse') {
    const hasMenuPage = options.pages?.some((page) => page.slug === PAGE_SLUG.menu);
    if (options.siteMode === 'standard' && hasMenuPage) {
      const locale = options.locale ?? options.defaultLocale ?? 'de';
      const defaultLocale = options.defaultLocale ?? 'de';
      return prefixPublicPath(
        buildLocalizedSitePath(publicPathForPageSlug(PAGE_SLUG.menu), locale, defaultLocale),
        options.publicPathPrefix,
      );
    }
    const hasMenuBlock = blocksInclude(options.allBlocks, MENU_COMPONENT_TYPES);
    if (hasMenuBlock === false) {
      return sectionAnchorHref('#contact', options);
    }
    return sectionAnchorHref('#menu', options);
  }

  if (raw === 'contact' || raw === 'inquire') {
    const hasContactPage = options.pages?.some((page) => page.slug === PAGE_SLUG.contact);
    if (options.siteMode === 'standard' && hasContactPage) {
      const locale = options.locale ?? options.defaultLocale ?? 'de';
      const defaultLocale = options.defaultLocale ?? 'de';
      return prefixPublicPath(
        buildLocalizedSitePath(publicPathForPageSlug(PAGE_SLUG.contact), locale, defaultLocale),
        options.publicPathPrefix,
      );
    }
    return sectionAnchorHref('#contact', options);
  }

  if (raw === 'route' || raw === 'visit') {
    return sectionAnchorHref('#location', options);
  }

  if (
    raw === 'order' ||
    raw === PLATFORM_CTA_INTENT.external ||
    raw === PLATFORM_CTA_INTENT.call ||
    raw === PLATFORM_CTA_INTENT.section
  ) {
    return sectionAnchorHref('#contact', options);
  }

  const normalized = normalizePlatformCtaIntent(raw);
  if (normalized === PLATFORM_CTA_INTENT.section) {
    return sectionAnchorHref('#contact', options);
  }
  if (
    normalized === PLATFORM_CTA_INTENT.external ||
    normalized === PLATFORM_CTA_INTENT.call
  ) {
    return sectionAnchorHref('#contact', options);
  }

  // Unknown → contact
  return sectionAnchorHref('#contact', options);
}

/**
 * Read `ctaIntent` from block content, else infer from href, else fallback.
 */
export function readBlockCtaIntent(
  content: Record<string, LocalizedString> | undefined,
  locale: string,
  options?: {
    /** Defaults to `ctaIntent`. Secondary buttons use `ctaSecondaryIntent`. */
    intentFieldKey?: string;
    hrefFieldKey?: string;
    fallback?: SectionHrefIntent;
  },
): PlatformCtaIntent {
  const intentKey = options?.intentFieldKey ?? 'ctaIntent';
  const raw = content ? pickBlockField(content, intentKey, locale).trim() : '';
  const fromField = normalizePlatformCtaIntent(raw);
  if (fromField) {
    return fromField;
  }
  if (content && options?.hrefFieldKey) {
    const href = pickBlockField(content, options.hrefFieldKey, locale).trim();
    const inferred = inferCtaIntentFromHref(href);
    if (inferred) {
      return inferred;
    }
  }
  return (
    normalizePlatformCtaIntent(options?.fallback ?? PLATFORM_CTA_INTENT.section) ??
    PLATFORM_CTA_INTENT.section
  );
}

/** Prefer optional content override (`ctaPrimaryHref` / `ctaHref`), else section intent. */
export function resolveBlockCtaHref(
  intent: SectionHrefIntent,
  options: ResolveSectionHrefOptions & {
    locale: string;
    content?: Record<string, LocalizedString>;
    hrefFieldKey?: string;
  },
): string {
  if (options.content && options.hrefFieldKey) {
    const override = pickBlockField(options.content, options.hrefFieldKey, options.locale).trim();
    if (override) {
      const defaultLocale = options.defaultLocale ?? 'de';
      const normalized = normalizeHomepageSectionHref(
        override,
        options.locale,
        defaultLocale,
        options.publicPathPrefix,
      );
      if (normalized) {
        return normalized;
      }
      return prefixPublicPath(override, options.publicPathPrefix);
    }
  }
  return resolveSectionHref(intent, options);
}
